import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { MapPin, Calendar, Briefcase } from "lucide-react";
import { SectionTitle } from "./shared";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem; /* This adds space between the left and right panels */

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr; /* Adjusts size ratio for large screens */
    gap: 3rem; /* More space between them */
  }
`;

const ExperienceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ExperienceCard = styled.div`
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${(props) => (props.$isActive ? "rgba(255, 255, 255, 0.05)" : "transparent")};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ExperienceCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ExperienceCardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0;
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  ${ExperienceCard}:hover & {
    opacity: 0.9;
  }
`;

const DetailsPanel = styled.div`
  position: relative;
  height: 600px;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  overflow: hidden;
`;

const DetailsPanelContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ExperienceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  white-space: pre-line;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const Experiences = ({ experiences }) => {
  const [activeExperience, setActiveExperience] = useState(experiences?.[0]);

  if (!experiences || experiences.length === 0) return null;

  return (
    <Container>
      <SectionTitle>Experiences</SectionTitle>
      <Grid>
        <ExperienceList>
          {experiences.map((exp) => (
            <ExperienceCard
              key={exp.id}
              $isActive={activeExperience?.id === exp.id}
              onMouseEnter={() => setActiveExperience(exp)}
            >
              <ExperienceCardContent>
                <ExperienceCardTitle>{exp.name}</ExperienceCardTitle>
              </ExperienceCardContent>
            </ExperienceCard>
          ))}
        </ExperienceList>

        <DetailsPanel>
          <DetailsPanelContent>
            {activeExperience ? (
              <ExperienceDetails>
                <Title>{activeExperience.name}</Title>
                <MetaInfo>
                  <Briefcase size={16} /> {activeExperience.company}
                </MetaInfo>
                <MetaInfo>
                  <MapPin size={16} /> {activeExperience.location}
                </MetaInfo>
                <MetaInfo>
                  <Calendar size={16} />
                  {activeExperience.start_date} - {activeExperience.end_date}
                </MetaInfo>
                <Description>{activeExperience.description}</Description>
              </ExperienceDetails>
            ) : (
              <Title>Experiences</Title>
            )}
          </DetailsPanelContent>
        </DetailsPanel>
      </Grid>
    </Container>
  );
};

export default Experiences;
